import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { keyed } from 'lit/directives/keyed.js';
import { NavigateBrowserTest, type NavigateTestSelection } from './NavigateBrowserTest.js';

const CHANNEL = 'zwickroell-navigate-v1';
type CapabilityState = { loading: boolean; enabled: boolean; error: string };

async function readCapabilities(): Promise<CapabilityState> {
  try {
    const response = await fetch('/api/navigate/capabilities', { credentials: 'same-origin', cache: 'no-store' });
    if (response.status === 404) return { loading: false, enabled: false, error: '' };
    if (!response.ok)
      throw new Error(
        response.status === 401 ? 'Bitte melden Sie sich erneut an.' : 'Navigate konnte nicht geladen werden.',
      );
    const body = await response.json();
    return { loading: false, enabled: body.enabled !== false, error: '' };
  } catch (error) {
    return {
      loading: false,
      enabled: false,
      error: error instanceof Error ? error.message : 'Navigate konnte nicht geladen werden.',
    };
  }
}

export const NavigateNavigation = stateful(() => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    void readCapabilities().then(result => {
      if (alive) setEnabled(result.enabled);
    });
    return () => {
      alive = false;
    };
  }, []);
  return enabled
    ? html`<a
        class="flex items-center gap-1 hover:text-primary"
        href="/navigate"
        title="KI-gesteuerter Gesprächsablauf · Browser-Test"
        >Navigate</a
      >`
    : nothing;
});

export const NavigatePage = stateful(() => {
  const [capabilities, setCapabilities] = useState<CapabilityState>({ loading: true, enabled: false, error: '' });
  const [frame] = useState<{ current: HTMLIFrameElement | null }>({ current: null });
  const [selection, setSelection] = useState<NavigateTestSelection | null>(null);
  const [bridge] = useState<{ active: boolean; requestId: string | null }>({ active: false, requestId: null });
  const sendFor = (requestId: string | null, status: string, details: Record<string, unknown> = {}) => {
    frame.current?.contentWindow?.postMessage(
      { channel: CHANNEL, type: 'test-status', requestId, status, ...details },
      window.location.origin,
    );
  };
  useEffect(() => {
    let alive = true;
    void readCapabilities().then(result => {
      if (alive) setCapabilities(result);
    });
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow) return;
      const data = event.data;
      if (!data || data.channel !== CHANNEL) return;
      if (data.type === 'test-stop') {
        if (bridge.requestId !== null && data.requestId === bridge.requestId) {
          sendFor(bridge.requestId, 'closed');
          bridge.active = false;
          bridge.requestId = null;
          setSelection(null); // Unmount stops this experimental call, never the normal Call page.
        }
        return;
      }
      if (data.type !== 'test-start') return;
      if (
        typeof data.requestId !== 'string' ||
        data.requestId.length > 128 ||
        !Number.isSafeInteger(data.draftSeq) ||
        data.draftSeq < 0
      )
        return;
      if (data.versionId !== undefined && (typeof data.versionId !== 'string' || data.versionId.length > 128)) return;
      if (bridge.active) {
        frame.current?.contentWindow?.postMessage(
          {
            channel: CHANNEL,
            type: 'test-status',
            requestId: data.requestId,
            status: 'error',
            error: 'Bitte beenden Sie den laufenden Browser-Test, bevor Sie einen neuen starten.',
          },
          window.location.origin,
        );
        return;
      }
      bridge.active = true;
      bridge.requestId = data.requestId;
      setSelection({ draftSeq: data.draftSeq, ...(data.versionId ? { versionId: data.versionId } : {}) });
      sendFor(bridge.requestId, 'ready');
    };
    window.addEventListener('message', onMessage);
    return () => {
      alive = false;
      window.removeEventListener('message', onMessage);
    };
  }, []);
  if (capabilities.loading) return html`<div class="p-8" role="status">Navigate wird geladen…</div>`;
  if (!capabilities.enabled)
    return html`<div class="p-8">
      <h1 class="text-2xl font-semibold">KI-gesteuerter Gesprächsablauf</h1>
      <p class="mt-3">${capabilities.error || 'Navigate ist in der Staging-Testumgebung verfügbar.'}</p>
      <a href="/call" class="btn btn-outline mt-4">Zur Anrufseite</a>
    </div>`;
  // Capture the owner: cleanup or delayed events from a previous child must not
  // release, close, or add logs to the next test request.
  const selectedRequestId = bridge.requestId;
  return html` <section class="zw-navigate-layout ${selection ? 'zw-navigate-testing' : ''}">
    <style>
      .zw-navigate-layout {
        height: calc(100dvh - 94px);
        min-height: 520px;
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 12px;
      }
      .zw-navigate-layout.zw-navigate-testing {
        grid-template-columns: minmax(0, 1fr) minmax(420px, 44%);
      }
      .zw-navigate-test-panel {
        min-width: 0;
        min-height: 0;
        overflow: auto;
        background: white;
        border: 1px solid #dce2e8;
        border-radius: 12px;
      }
      @media (max-width: 1000px) {
        .zw-navigate-layout.zw-navigate-testing {
          grid-template-columns: minmax(0, 1fr);
          height: auto;
        }
        .zw-navigate-layout.zw-navigate-testing iframe {
          min-height: 520px;
        }
        .zw-navigate-test-panel {
          max-height: 85dvh;
        }
      }
    </style>
    <iframe
      ${ref(element => {
        frame.current = element as HTMLIFrameElement | null;
      })}
      src="/navigate-builder/index.html"
      title="Navigate — Editor für KI-gesteuerte Gesprächsabläufe"
      style="border:0;width:100%;height:100%;background:#f5f5f5;flex:1"
    ></iframe>
    ${selection
      ? html` <aside class="zw-navigate-test-panel" aria-label="Navigate-Browser-Test">
          ${keyed(
            selectedRequestId,
            NavigateBrowserTest({
              selection,
              onStatus: (status, details = {}) => {
                if (bridge.requestId !== selectedRequestId) return;
                // An ended transcript remains visible. Both a new builder test and
                // the existing round-button retry can acquire the slot again.
                if (status === 'stopped' || status === 'error') bridge.active = false;
                else if (status === 'connecting' || status === 'connected') bridge.active = true;
                sendFor(selectedRequestId, status, details);
              },
              onEvent: (type, payload) => {
                if (bridge.requestId !== selectedRequestId) return;
                frame.current?.contentWindow?.postMessage(
                  { channel: CHANNEL, type, requestId: selectedRequestId, ...payload },
                  window.location.origin,
                );
              },
              onClose: () => {
                if (bridge.requestId !== selectedRequestId) return;
                sendFor(selectedRequestId, 'closed');
                bridge.active = false;
                bridge.requestId = null;
                setSelection(null);
              },
            }),
          )}
        </aside>`
      : nothing}
  </section>`;
});
